/**
 * Frontend mirror of the backend matrices in
 * `src/api/models/meta_ad_set.py` — kept in sync manually (the backend
 * is the security boundary; this file gates UI options for UX).
 *
 * If you edit one side, edit the other. Backend will reject mismatches
 * with 400 (router validates after parent-campaign lookup), so a drift
 * surfaces as a Meta-side "Invalid combination" error rather than a
 * silent shipping bug. Still: mirror.
 */

import type {
  MetaBidStrategy,
  MetaBillingEvent,
  MetaCampaignObjective,
  MetaDestinationType,
  MetaSpecialAdCategory,
  MetaOptimizationGoal,
} from '@/services/api/metaAds';

/** Which optimization goals are valid under each parent campaign objective.
 *  Used to filter the `optimization_goal` dropdown options in
 *  `AdSetBasicsStep.tsx` based on the parent campaign's objective.
 *
 *  Mirrors `OBJECTIVE_TO_OPTIMIZATION_GOALS` in `meta_ad_set.py`. */
export const OBJECTIVE_TO_OPTIMIZATION_GOALS: Record<
  MetaCampaignObjective,
  MetaOptimizationGoal[]
> = {
  OUTCOME_AWARENESS: ['REACH', 'IMPRESSIONS', 'AD_RECALL_LIFT', 'THRUPLAY'],
  OUTCOME_TRAFFIC: [
    'LINK_CLICKS',
    'LANDING_PAGE_VIEWS',
    'IMPRESSIONS',
    'REACH',
  ],
  OUTCOME_ENGAGEMENT: [
    'POST_ENGAGEMENT',
    'PAGE_LIKES',
    'EVENT_RESPONSES',
    'THRUPLAY',
    'VIDEO_VIEWS',
    'REACH',
    'IMPRESSIONS',
    'CONVERSATIONS',
  ],
  OUTCOME_LEADS: [
    'LEAD_GENERATION',
    'QUALITY_LEAD',
    'OFFSITE_CONVERSIONS',
    'LANDING_PAGE_VIEWS',
    'CONVERSATIONS',
  ],
  OUTCOME_SALES: [
    'OFFSITE_CONVERSIONS',
    'VALUE',
    'LANDING_PAGE_VIEWS',
    'REACH',
  ],
  OUTCOME_APP_PROMOTION: [
    'APP_INSTALLS',
    'OFFSITE_CONVERSIONS',
    'VALUE',
    'LINK_CLICKS',
  ],
};

/** Which billing events are valid under each optimization goal.
 *  Used to filter the `billing_event` dropdown in `AdSetBasicsStep.tsx`
 *  based on the user's selected goal.
 *
 *  Order matters: the first entry is the wizard's default and the first
 *  option shown in the dropdown. IMPRESSIONS is listed first wherever
 *  it's allowed — it is universally accepted by Meta (including on new
 *  ad accounts that don't yet qualify for goal-matching billing like
 *  LINK_CLICKS). Putting the safe option first eliminates the "Billing
 *  Option Not Available" stumbling block while keeping the goal-matching
 *  billing event in the list for experienced advertisers who want it.
 *
 *  Mirrors `OPTIMIZATION_GOAL_TO_BILLING_EVENTS` in `meta_ad_set.py`. */
export const OPTIMIZATION_GOAL_TO_BILLING_EVENTS: Record<
  MetaOptimizationGoal,
  MetaBillingEvent[]
> = {
  REACH: ['IMPRESSIONS'],
  IMPRESSIONS: ['IMPRESSIONS'],
  AD_RECALL_LIFT: ['IMPRESSIONS'],
  LINK_CLICKS: ['IMPRESSIONS', 'LINK_CLICKS'],
  // LANDING_PAGE_VIEWS only bills IMPRESSIONS — Meta rejects LINK_CLICKS
  // billing here ("Billing event invalid for optimization goal").
  LANDING_PAGE_VIEWS: ['IMPRESSIONS'],
  POST_ENGAGEMENT: ['IMPRESSIONS', 'POST_ENGAGEMENT'],
  PAGE_LIKES: ['IMPRESSIONS', 'PAGE_LIKES'],
  EVENT_RESPONSES: ['IMPRESSIONS'],
  THRUPLAY: ['IMPRESSIONS', 'THRUPLAY'],
  VIDEO_VIEWS: ['IMPRESSIONS'],
  OFFSITE_CONVERSIONS: ['IMPRESSIONS'],
  VALUE: ['IMPRESSIONS'],
  LEAD_GENERATION: ['IMPRESSIONS'],
  QUALITY_LEAD: ['IMPRESSIONS'],
  APP_INSTALLS: ['IMPRESSIONS', 'APP_INSTALLS'],
  CONVERSATIONS: ['IMPRESSIONS'],
};

/** Objectives whose ad sets MUST carry a `promoted_object`. The wizard
 *  renders the PromotedObject step only for these. Mirrors
 *  `OBJECTIVES_REQUIRING_PROMOTED_OBJECT` in `meta_ad_set.py`. */
export const OBJECTIVES_REQUIRING_PROMOTED_OBJECT: ReadonlySet<MetaCampaignObjective> =
  new Set<MetaCampaignObjective>([
    'OUTCOME_SALES',
    'OUTCOME_LEADS',
    'OUTCOME_APP_PROMOTION',
  ]);

/** Campaign-level bid strategies that require ad sets to carry a
 *  `bid_amount` cap / floor. Mirrors `CONSTRAINED_BID_STRATEGIES` in
 *  `meta_ad_set.py`. The wizard shows the `bid_amount` field only when
 *  the parent campaign's bid_strategy is in this set. */
export const CONSTRAINED_BID_STRATEGIES: ReadonlySet<MetaBidStrategy> =
  new Set<MetaBidStrategy>([
    'LOWEST_COST_WITH_BID_CAP',
    'COST_CAP',
    'LOWEST_COST_WITH_MIN_ROAS',
  ]);

/** Special ad categories that force `targeting.age_min >= 18` per Meta
 *  policy. Wizard locks the age slider to 18+ when the parent campaign
 *  carries any of these. Mirrors `AGE_RESTRICTED_CATEGORIES` in
 *  `meta_ad_set.py`. */
export const AGE_RESTRICTED_CATEGORIES: ReadonlySet<MetaSpecialAdCategory> =
  new Set<MetaSpecialAdCategory>([
    'HOUSING',
    'EMPLOYMENT',
    'CREDIT',
  ]);

/** Destination types Meta allows under each objective. The wizard's
 *  destination_type dropdown filters against this. For objectives where
 *  Meta picks a default and the surface doesn't need exposure, leave
 *  the array empty so the dropdown doesn't render. */
export const OBJECTIVE_TO_DESTINATION_TYPES: Record<
  MetaCampaignObjective,
  MetaDestinationType[]
> = {
  OUTCOME_AWARENESS: [],
  OUTCOME_TRAFFIC: ['WEBSITE', 'APP', 'MESSENGER', 'WHATSAPP', 'PHONE_CALL'],
  OUTCOME_ENGAGEMENT: ['ON_AD', 'ON_POST', 'ON_PAGE', 'MESSENGER'],
  OUTCOME_LEADS: ['ON_AD', 'WEBSITE', 'MESSENGER', 'INSTAGRAM_DIRECT', 'PHONE_CALL'],
  OUTCOME_SALES: ['WEBSITE', 'APP', 'MESSENGER'],
  OUTCOME_APP_PROMOTION: ['APP', 'WEBSITE'],
};
